import { useMemo, useState } from "react";
import { CampoNumero, CampoSelect, Pestanas, Resultado, Dato, Rejilla, mxn } from "../components/campos.jsx";
import { amortizarInfonavit, propuestaInfonavit, pagoMensual } from "../lib/calc.js";
import { UMA, APORTACION_PATRONAL_INFONAVIT_PCT } from "../data/fiscal.js";

export default function Infonavit() {
  const [monto, setMonto] = useState(600000);
  const [sueldo, setSueldo] = useState(18000);
  const [plazo, setPlazo] = useState(20);
  const [tasaManual, setTasaManual] = useState(null);
  const [modoExtra, setModoExtra] = useState("regular");
  const [extraMonto, setExtraMonto] = useState(20000);
  const [frecuencia, setFrecuencia] = useState("anual");
  const [anioInicio, setAnioInicio] = useState(1);
  const [anioFin, setAnioFin] = useState(20);
  const [mesUnico, setMesUnico] = useState(12);

  const propuesta = useMemo(() => propuestaInfonavit(sueldo), [sueldo]);
  const tasa = tasaManual ?? propuesta.tasaAnual;

  const pagosExtra = useMemo(
    () =>
      modoExtra === "regular"
        ? { modo: "regular", monto: extraMonto, frecuencia, anioInicio, anioFin }
        : { modo: "unico", monto: extraMonto, mesUnico },
    [modoExtra, extraMonto, frecuencia, anioInicio, anioFin, mesUnico],
  );

  const base = { monto, tasaAnual: tasa, plazoAnios: plazo, sueldoMensual: sueldo };

  // Tres escenarios comparables: el crédito tal cual, el mismo crédito
  // con la aportación patronal del 5%, y ese más los pagos extra.
  const esc1 = useMemo(() => amortizarInfonavit({ ...base, aplicarAportacionPatronal: false }), [monto, tasa, plazo, sueldo]);
  const esc2 = useMemo(() => amortizarInfonavit({ ...base, aplicarAportacionPatronal: true }), [monto, tasa, plazo, sueldo]);
  const esc3 = useMemo(
    () => amortizarInfonavit({ ...base, aplicarAportacionPatronal: true, pagosExtra }),
    [monto, tasa, plazo, sueldo, pagosExtra],
  );

  const pago = pagoMensual(monto, tasa, plazo);
  const ahorroMeses = Math.max(0, esc1.meses - esc3.meses);
  const ahorroIntereses = Math.max(0, esc1.interesTotal - esc3.interesTotal);
  const excedeDescuento = pago > propuesta.descuentoPropuesto;

  return (
    <div className="space-y-6">
      <Rejilla>
        <div className="space-y-4">
          <CampoNumero label="Monto del crédito" valor={monto} onChange={setMonto} prefijo="$" min={0} />
          <CampoNumero label="Tu sueldo mensual bruto" valor={sueldo} onChange={setSueldo} prefijo="$" min={0} />
          <CampoNumero label="Plazo" valor={plazo} onChange={setPlazo} sufijo="años" min={1} max={30} step="1" />
          <CampoNumero
            label="Tasa anual"
            valor={tasa}
            onChange={setTasaManual}
            sufijo="%"
            min={0}
            ayuda={`Sugerida por tu ingreso: ${propuesta.tasaAnual}%. Puedes ajustarla.`}
          />

          <div className="rounded-xl border border-[color:var(--color-marca-300)] superficie-2 p-3">
            <span className="block text-2xs font-bold uppercase tracking-wider texto-3">
              Propuesta según tu ingreso
            </span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Mini label="Tasa sugerida" valor={`${propuesta.tasaAnual}%`} />
              <Mini label="Descuento máx." valor={`${propuesta.descuentoMaximoPct}%`} />
            </div>
            <div className="mt-2 flex justify-between text-2xs texto-3">
              <span>Ref: <strong className="texto-2">{propuesta.umas.toFixed(2)} UMAs</strong></span>
              <span>UMA mensual: {mxn(UMA.mensual)}</span>
            </div>
          </div>
        </div>

        <div className="order-first space-y-3 lg:order-last lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <Resultado label="Pago mensual estimado" valor={mxn(pago)} nota={`${plazo} años · ${tasa}% anual`} />
            <Resultado
              label="Descuento máximo permitido"
              valor={mxn(propuesta.descuentoPropuesto)}
              tono={excedeDescuento ? "alerta" : "positivo"}
              nota={`${propuesta.descuentoMaximoPct}% de tu sueldo`}
            />
          </div>

          {excedeDescuento && (
            <p className="rounded-xl border border-[color:var(--alerta-borde)] bg-[color:var(--alerta-bg)] p-3 text-2xs text-[color:var(--alerta)]">
              El pago mensual supera el descuento máximo del {propuesta.descuentoMaximoPct}% sobre tu
              sueldo. Con este ingreso, el INFONAVIT no autorizaría el crédito en estos términos:
              tendrías que alargar el plazo o reducir el monto.
            </p>
          )}

          <div className="tarjeta">
            <h3 className="mb-2 text-2xs font-bold uppercase tracking-wider texto-3">
              Aportación patronal ({APORTACION_PATRONAL_INFONAVIT_PCT}% de tu sueldo)
            </h3>
            <Dato label="Abono mensual a capital" valor={mxn(sueldo * 0.05)} />
            <Dato label="Total aportado durante el crédito" valor={mxn(esc2.aportacionPatronalTotal)} destacar />
          </div>
        </div>
      </Rejilla>

      <section>
        <h3 className="mb-3 font-bold text-[color:var(--texto)]">Pagos extra voluntarios</h3>
        <Pestanas
          valor={modoExtra}
          onChange={setModoExtra}
          opciones={[
            { valor: "regular", etiqueta: "Abonos periódicos" },
            { valor: "unico", etiqueta: "Un solo pago" },
          ]}
        />

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <CampoNumero label="Monto del abono" valor={extraMonto} onChange={setExtraMonto} prefijo="$" min={0} />
          {modoExtra === "regular" ? (
            <>
              <CampoSelect
                label="Frecuencia"
                valor={frecuencia}
                onChange={setFrecuencia}
                opciones={[
                  { valor: "anual", etiqueta: "Una vez al año" },
                  { valor: "mensual", etiqueta: "Cada mes" },
                ]}
              />
              <CampoNumero label="Desde el año" valor={anioInicio} onChange={setAnioInicio} min={1} step="1" />
              <CampoNumero label="Hasta el año" valor={anioFin} onChange={setAnioFin} min={1} step="1" />
            </>
          ) : (
            <CampoNumero label="En el mes número" valor={mesUnico} onChange={setMesUnico} min={1} step="1" />
          )}
        </div>
      </section>

      <section>
        <h3 className="mb-3 font-bold text-[color:var(--texto)]">Comparación de los 3 escenarios</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <Escenario titulo="Sólo tu pago" r={esc1} monto={monto} />
          <Escenario titulo="+ Aportación patronal" r={esc2} monto={monto} referencia={esc1} />
          <Escenario titulo="+ Pagos extra" r={esc3} monto={monto} referencia={esc1} destacado />
        </div>
      </section>

      {ahorroMeses > 0 && (
        <div className="rounded-2xl border border-[color:var(--positivo-borde)] bg-[color:var(--positivo-bg)] p-5 text-center">
          <span className="block text-2xs font-semibold uppercase tracking-wider text-[color:var(--positivo)]">
            Con tu estrategia de pagos extra
          </span>
          <strong className="mt-1 block font-mono text-2xl font-extrabold text-[color:var(--positivo)]">
            Ahorras {mxn(ahorroIntereses, 0)} en intereses
          </strong>
          <span className="mt-1 block text-sm text-[color:var(--positivo)]">
            y liquidas {Math.floor(ahorroMeses / 12)} años {ahorroMeses % 12} meses antes.
          </span>
        </div>
      )}

      {esc3.tabla.length > 0 && (
        <details className="tarjeta">
          <summary className="cursor-pointer text-sm font-medium texto-2">
            Ver tabla de amortización completa
          </summary>
          <div className="mt-3 max-h-96 overflow-auto">
            <table className="w-full text-left text-2xs">
              <thead className="sticky top-0 superficie texto-3">
                <tr>
                  <th className="py-1.5 pr-2 font-semibold">Mes</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Pago</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Interés</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Capital</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Patronal</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Extra</th>
                  <th className="py-1.5 text-right font-semibold">Saldo</th>
                </tr>
              </thead>
              <tbody className="font-mono texto-2">
                {esc3.tabla.map((f) => (
                  <tr key={f.mes} className="border-t borde">
                    <td className="py-1.5 pr-2">{f.mes}</td>
                    <td className="py-1.5 pr-2 text-right">{mxn(f.pago, 0)}</td>
                    <td className="py-1.5 pr-2 text-right text-[color:var(--alerta)]">{mxn(f.interes, 0)}</td>
                    <td className="py-1.5 pr-2 text-right text-[color:var(--positivo)]">{mxn(f.capital, 0)}</td>
                    <td className="py-1.5 pr-2 text-right acento">{mxn(f.aportacionPatronal, 0)}</td>
                    <td className="py-1.5 pr-2 text-right text-[color:var(--aviso)]">{f.extra ? mxn(f.extra, 0) : "—"}</td>
                    <td className="py-1.5 text-right text-[color:var(--texto)]">{mxn(f.saldoFinal, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function Mini({ label, valor }) {
  return (
    <div className="rounded border borde superficie-2 p-2 text-center">
      <span className="block text-[9px] font-semibold uppercase texto-3">{label}</span>
      <span className="mt-0.5 block font-mono text-xs font-extrabold acento">{valor}</span>
    </div>
  );
}

function Escenario({ titulo, r, monto, referencia, destacado }) {
  const anios = Math.floor(r.meses / 12);
  const meses = r.meses % 12;
  const mejora = referencia ? referencia.meses - r.meses : 0;

  return (
    <div className={`tarjeta ${destacado ? "border-[color:var(--positivo-borde)]" : ""}`}>
      <h4 className="text-sm font-bold text-[color:var(--texto)]">{titulo}</h4>
      {!r.liquidado ? (
        <p className="mt-3 text-2xs text-[color:var(--alerta)]">
          Con estas condiciones el crédito no se liquida: el pago no alcanza a cubrir los intereses.
        </p>
      ) : (
        <>
          <p className="mt-3 font-mono text-2xl font-extrabold text-[color:var(--texto)]">
            {anios}a {meses}m
          </p>
          <span className="text-2xs texto-3">tiempo para liquidar</span>
          <div className="mt-3">
            <Dato label="Intereses pagados" valor={mxn(r.interesTotal, 0)} />
            <Dato label="Total desembolsado" valor={mxn(monto + r.interesTotal, 0)} />
            {mejora > 0 && (
              <p className="mt-2 text-2xs font-semibold text-[color:var(--positivo)]">
                {Math.floor(mejora / 12)}a {mejora % 12}m antes que el escenario base
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
